<template>
  <div :class="$style.overlay" v-if="isVisible">
    <div :class="$style.dialog">
      <div :class="$style.header">
        <h3>로그인</h3>
        <button :class="$style.closeButton" @click="handleClose">✕</button>
      </div>
      
      <div :class="$style.content">
        <p>주문 후 문의를 위해 로그인이 필요합니다.</p>
        
        <div :class="$style.form">
          <div :class="$style.formGroup">
            <label for="userId">아이디</label>
            <input 
              type="text" 
              id="userId" 
              v-model="userId" 
              placeholder="아이디 입력"
              :class="$style.input"
              :disabled="loading"
            />
          </div>
          
          <div :class="$style.formGroup">
            <label for="password">비밀번호</label>
            <input 
              type="password" 
              id="password" 
              v-model="password" 
              placeholder="비밀번호 입력"
              :class="$style.input"
              :disabled="loading"
            />
          </div>
          
          <div :class="$style.error" v-if="errorMessage">
            {{ errorMessage }}
          </div>
        </div>
      </div>
      
      <div :class="$style.footer">
        <button 
          :class="$style.cancelButton" 
          @click="handleClose"
          :disabled="loading"
        >
          취소
        </button>
        <button 
          :class="$style.loginButton" 
          @click="handleLogin"
          :disabled="!isValid || loading"
        >
          {{ loading ? '로그인 중...' : '로그인' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useAuth } from '@/composables/useAuth';
import type { User } from 'firebase/auth';

// Props
const props = defineProps<{
  isVisible: boolean;
}>();

// Emits
const emit = defineEmits<{ 
  (e: 'close'): void 
  (e: 'login-success', user: User | null): void
}>();

// State
const userId = ref('TVRBNE1EazA=');
const password = ref('MjAyMS0wNC0wNyAyMjoxMg==');
const errorMessage = ref('');
const loading = ref(false);

// Auth composable
const { authenticateWithCredentials } = useAuth();

// Computed
const isValid = computed(() => {
  return userId.value.trim() !== '' && password.value.trim() !== '';
});

// Methods
const handleClose = () => {
  // 입력값 초기화
  userId.value = ''; // 또는 'TVRBNE1EazA='
  password.value = ''; // 또는 'MjAyMS0wNC0wNyAyMjoxMg=='
  errorMessage.value = '';
  
  // 닫기 이벤트 발생
  emit('close');
};

const handleLogin = async () => {
  // 유효성 검사
  if (!isValid.value) return;
  
  // 로딩 상태 설정
  loading.value = true;
  errorMessage.value = '';
  
  try {
    console.log('[LoginDialog] Attempting login...');
    const loggedInUser = await authenticateWithCredentials(userId.value, password.value);
    console.log('[LoginDialog] authenticateWithCredentials result (User):', loggedInUser?.uid ?? 'null');

    if (loggedInUser) {
      console.log('[LoginDialog] Login successful, emitting event with user...');
      emit('login-success', loggedInUser);
      handleClose();
    } else {
      console.log('[LoginDialog] authenticateWithCredentials returned null.');
      errorMessage.value = '로그인에 실패했습니다. 아이디 또는 비밀번호를 확인해주세요.';
    }
  } catch (error: any) {
    console.error('[LoginDialog] Login failed with error:', error);
    errorMessage.value = error.message || '로그인 중 오류가 발생했습니다. 다시 시도해주세요.';
  } finally {
    loading.value = false;
  }
};
</script>

<style module scoped lang="less">
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.dialog {
  width: 100%;
  max-width: 400px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

.header {
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #eee;
  
  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
  }
}

.closeButton {
  background: transparent;
  border: none;
  font-size: 18px;
  color: #666;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  
  &:hover {
    color: #333;
  }
}

.content {
  padding: 20px;
  
  p {
    margin: 0 0 20px 0;
    color: #555;
    font-size: 14px;
  }
}

.form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.formGroup {
  display: flex;
  flex-direction: column;
  gap: 6px;
  
  label {
    font-size: 14px;
    font-weight: 500;
    color: #333;
  }
}

.input {
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #CF1578;
  }
  
  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
}

.error {
  color: #e53e3e;
  font-size: 13px;
  margin-top: 4px;
}

.footer {
  padding: 16px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid #eee;
}

.cancelButton, 
.loginButton {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.cancelButton {
  background-color: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
  
  &:hover:not(:disabled) {
    background-color: #eee;
  }
}

.loginButton {
  background-color: #CF1578;
  color: white;
  border: none;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
}
</style> 